/**
 * CommentThread
 *
 * Renders one root comment and its replies (depth-1 only).
 * Delegates rendering to CommentItem for each node.
 */

import CommentItem from './CommentItem';
import type { EvaluationComment } from '@/services/commentApi';

interface CommentThreadProps {
  comment: EvaluationComment;
  currentUserId: string | null;
  currentUserRole: string | null;
  onReply: (comment: EvaluationComment) => void;
  onEdit: (comment: EvaluationComment, newBody: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  onReport: (commentId: string) => void;
  onSeekToTimestamp?: (sec: number) => void;
}

export default function CommentThread({
  comment,
  currentUserId,
  currentUserRole,
  onReply,
  onEdit,
  onDelete,
  onReport,
  onSeekToTimestamp,
}: CommentThreadProps) {
  const sharedProps = {
    currentUserId,
    currentUserRole,
    onEdit,
    onDelete,
    onReport,
    onSeekToTimestamp,
  };

  return (
    <div className="border-b border-border-subtle last:border-b-0 py-1">
      {/* Root comment */}
      <CommentItem
        comment={comment}
        onReply={onReply}
        {...sharedProps}
      />

      {/* Replies — depth 1, always shown in full */}
      {comment.replies.length > 0 && (
        <div className="mt-1">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              isReply
              {...sharedProps}
            />
          ))}
        </div>
      )}
    </div>
  );
}
